import { Box, Divider, Input, Text } from "@wix/design-system";
import lodash from "lodash";
import React, { useContext, useRef } from "react";
import { EditorContext, ModalServiceContext, RicosContext } from "./../../ricos-context";
import { toHexFormat } from "./../../ricos-styles";
import { useBiNext } from "./../../_shared/bi-context-provider/bi-context-provider";
import { BorderColorPicker } from "./../components/border-color-picker";
import { ButtonColorSwatches, SliderWithInput } from "./../../ui-components";
import { IMAGE_EDITOR_COLOR_PICKER_MODAL_ID, IMAGE_SETTINGS_MODAL_ID } from "../consts";
import {
  SETTINGS_DESIGN_TAB_BORDER_COLOR_PICKER,
  SETTINGS_DESIGN_TAB_BORDER_WIDTH_INPUT,
  SETTINGS_DESIGN_TAB_BORDER_WIDTH_SLIDER,
  SETTINGS_DESIGN_TAB_CORNER_RADIUS_INPUT,
  SETTINGS_DESIGN_TAB_CORNER_RADIUS_SLIDER
} from "../data-hooks";
const DesignTab = ({ pmNodeAttrs, patchPmNodeAttrs, nodeId, config }) => {
  const { t, isMobile } = useContext(RicosContext);
  const modalService = useContext(ModalServiceContext) || {};
  const { adapter } = useContext(EditorContext);
  const moreColorsButtonRef = useRef(null);
  const logBi = useBiNext({ modalId: IMAGE_SETTINGS_MODAL_ID, pluginId: "image", nodeId });
  const image = nodeId ? adapter.tiptapEditor.view.dom.querySelector(`[data-hook=image-viewer-${nodeId}`) : void 0;
  const imageStyle = image ? window.getComputedStyle(image) : void 0;
  const defaultBorderColor = config.colors?.color8 ?? (imageStyle?.getPropertyValue("--ricos-action-color") || "#116DFF");
  const colorValue = imageStyle?.getPropertyValue("border-color");
  const borderColor = colorValue ? toHexFormat(colorValue) : void 0;
  const borderColorValue = pmNodeAttrs.styles?.border.color ?? borderColor ?? defaultBorderColor ?? void 0;
  const borderRadiusValue = pmNodeAttrs.styles?.border.radius ?? parseToInt(imageStyle?.getPropertyValue("border-radius")) ?? 0;
  const borderWidthValue = pmNodeAttrs.styles?.border.width ?? parseToInt(imageStyle?.getPropertyValue("border-width")) ?? 0;
  const openColorPicker = () => {
    const modalId = IMAGE_EDITOR_COLOR_PICKER_MODAL_ID;
    const referenceElement = moreColorsButtonRef.current;
    modalService.togglePluginPanel({
      id: modalId,
      Component: BorderColorPicker,
      componentProps: {
        modalId,
        nodeId,
        referenceElement,
        config,
        defaultColor: borderColorValue,
        onColorSelected: (color) => patchPmNodeAttrs({
          styles: {
            border: {
              color: color ?? pmNodeAttrs.styles?.border.color ?? null,
              radius: pmNodeAttrs.styles?.border.radius ?? null,
              width: pmNodeAttrs.styles?.border.width ?? null
            }
          }
        }),
        onColorAdded: (color) => config.onBorderColorAdded?.(color)
      },
      isMobile,
      positioning: {
        referenceElement,
        placement: "left"
      },
      type: void 0,
      onClickOutside: lodash.noop,
      focusReturnElement: referenceElement ?? void 0
    });
  };
  return /* @__PURE__ */ React.createElement(Box, { direction: "vertical", gap: "SP3", overflow: "auto", paddingTop: "SP3", paddingBottom: "SP3" }, /* @__PURE__ */ React.createElement(Box, { direction: "vertical", gap: "SP2", paddingLeft: "SP3", paddingRight: "SP3" }, /* @__PURE__ */ React.createElement(Box, { paddingBottom: "SP1" }, /* @__PURE__ */ React.createElement(Text, { size: "small", weight: "bold" }, t("ImagePlugin_Design_Corners"))), /* @__PURE__ */ React.createElement(
    SliderWithInput,
    {
      min: 0,
      max: 30,
      onChange: (radius) => patchPmNodeAttrs({
        styles: {
          border: {
            radius,
            color: pmNodeAttrs.styles?.border.color ?? null,
            width: pmNodeAttrs.styles?.border.width ?? null
          }
        }
      }),
      logBi,
      label: t("ImagePlugin_Design_Corner_Radius"),
      sliderDataHook: SETTINGS_DESIGN_TAB_CORNER_RADIUS_SLIDER,
      inputDataHook: SETTINGS_DESIGN_TAB_CORNER_RADIUS_INPUT,
      value: borderRadiusValue,
      suffix: /* @__PURE__ */ React.createElement(Input.Affix, null, t("Common_Units_PX"))
    }
  )), /* @__PURE__ */ React.createElement(Divider, null), /* @__PURE__ */ React.createElement(Box, { direction: "vertical", gap: "SP2", paddingLeft: "SP3", paddingRight: "SP3" }, /* @__PURE__ */ React.createElement(Box, { paddingBottom: "SP1" }, /* @__PURE__ */ React.createElement(Text, { size: "small", weight: "bold" }, t("ImagePlugin_Design_Border"))), /* @__PURE__ */ React.createElement(Box, { paddingBottom: "SP3" }, /* @__PURE__ */ React.createElement(
    ButtonColorSwatches,
    {
      label: t("ImagePlugin_Design_Border_Color"),
      color: borderColorValue,
      onChange: (color) => {
        logBi({ dataHook: SETTINGS_DESIGN_TAB_BORDER_COLOR_PICKER, value: color });
        patchPmNodeAttrs({
          styles: {
            border: {
              color,
              radius: pmNodeAttrs.styles?.border?.radius ?? null,
              width: pmNodeAttrs.styles?.border?.width ?? null
            }
          }
        });
      },
      dataHook: SETTINGS_DESIGN_TAB_BORDER_COLOR_PICKER,
      defaultColor: borderColorValue,
      onMorePress: () => {
        logBi({ dataHook: SETTINGS_DESIGN_TAB_BORDER_COLOR_PICKER });
        openColorPicker();
      },
      moreButtonRef: moreColorsButtonRef
    }
  )), /* @__PURE__ */ React.createElement(
    SliderWithInput,
    {
      min: 0,
      max: 15,
      onChange: (width) => {
        patchPmNodeAttrs({
          styles: {
            border: {
              width,
              color: pmNodeAttrs.styles?.border.color ?? null,
              radius: pmNodeAttrs.styles?.border.radius ?? null
            }
          }
        });
      },
      logBi,
      label: t("ImagePlugin_Design_Border_Width"),
      sliderDataHook: SETTINGS_DESIGN_TAB_BORDER_WIDTH_SLIDER,
      inputDataHook: SETTINGS_DESIGN_TAB_BORDER_WIDTH_INPUT,
      value: borderWidthValue,
      suffix: /* @__PURE__ */ React.createElement(Input.Affix, null, t("Common_Units_PX"))
    }
  )));
};
const parseToInt = (value) => {
  if (value === void 0) {
    return void 0;
  }
  const parsedValue = parseInt(value);
  return isNaN(parsedValue) ? void 0 : parsedValue;
};
export {
  DesignTab
};
//# sourceMappingURL=design-tab.js.map
